import { createContext, useContext, useEffect, useState, useCallback, useRef, PropsWithChildren, FC } from 'react'
import keycloak from '../keycloak'
import type { KeycloakProfile, KeycloakTokenParsed } from 'keycloak-js'
import { DEV_BYPASS_KEYCLOAK, DEV_STATIC_TOKEN, decodeTokenPayload } from '@/devAuth'
import { CircularProgress } from '@mui/material'

type AuthContextType = {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | undefined
  user: KeycloakProfile | undefined
  tokenParsed: KeycloakTokenParsed | undefined
  error: Error | null
  logout: () => Promise<void>
  updateToken: (minValidity?: number) => Promise<boolean>
  hasRole: (role: string) => boolean
  hasResourceRole: (role: string, resource?: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type AuthProviderProps = PropsWithChildren<{
  loadingComponent?: React.ReactNode
  errorComponent?: React.ReactNode
  initOptions?: Keycloak.KeycloakInitOptions
  onAuthSuccess?: () => void
  onAuthError?: (error: Error) => void
  onTokenExpired?: () => void
}>

export const AuthProvider: FC<AuthProviderProps> = ({
  children,
  loadingComponent = <DefaultLoadingScreen />,
  errorComponent = <DefaultErrorScreen />,
  initOptions = {
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false,
    flow: 'standard',
    responseMode: 'query',
  },
  onAuthSuccess,
  onAuthError,
  onTokenExpired,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [user, setUser] = useState<KeycloakProfile | undefined>()
  const [bypassTokenParsed, setBypassTokenParsed] = useState<KeycloakTokenParsed | undefined>()
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    const initKeycloak = async () => {
      if (DEV_BYPASS_KEYCLOAK && DEV_STATIC_TOKEN) {
        setIsAuthenticated(true)
        const payload = decodeTokenPayload(DEV_STATIC_TOKEN)
        setUser(
          payload ? { id: (payload.sub as string) ?? '', username: payload.preferred_username as string } : undefined,
        )
        setBypassTokenParsed(
          payload
            ? ({ ...payload, userId: (payload.userId ?? payload.sub) as string } as KeycloakTokenParsed)
            : undefined,
        )
        setIsLoading(false)
        onAuthSuccess?.()
        return
      }

      try {
        const authenticated = await keycloak.init(initOptions)

        if (!authenticated) {
          // This shouldn't happen with onLoad: 'login-required'
          // but just in case, redirect to login
          await keycloak.login()
          return
        }

        setIsAuthenticated(true)

        // Load user profile
        try {
          const profile = await keycloak.loadUserProfile()
          setUser(profile)
        } catch (err) {
          console.error('Failed to load user profile:', err)
        }

        // Setup token refresh
        keycloak.onTokenExpired = () => {
          onTokenExpired?.()
          keycloak
            .updateToken(30)
            .then((refreshed) => {
              if (refreshed) {
                console.log('Token refreshed')
              }
            })
            .catch(() => {
              console.error('Failed to refresh token')
              keycloak.logout()
            })
        }

        // Setup auto token refresh interval
        const tokenRefreshInterval = setInterval(() => {
          keycloak.updateToken(70).catch(() => {
            console.error('Failed to refresh token')
            keycloak.clearToken()
          })
        }, 60000)

        onAuthSuccess?.()

        // Cleanup function
        return () => clearInterval(tokenRefreshInterval)
      } catch (err) {
        console.error('Keycloak initialization failed:', err)
        setError(err as Error)
        onAuthError?.(err as Error)
      } finally {
        setIsLoading(false)
      }
    }

    initKeycloak()
  }, [initOptions, onAuthSuccess, onAuthError, onTokenExpired])

  const logout = useCallback(async () => {
    if (DEV_BYPASS_KEYCLOAK) return
    await keycloak.logout()
  }, [])

  const updateToken = useCallback(async (minValidity = 5): Promise<boolean> => {
    if (DEV_BYPASS_KEYCLOAK) return true
    try {
      return await keycloak.updateToken(minValidity)
    } catch (error) {
      console.error('Failed to update token:', error)
      throw error
    }
  }, [])

  const hasRole = useCallback((role: string): boolean => {
    if (DEV_BYPASS_KEYCLOAK) return true
    return keycloak.hasRealmRole(role)
  }, [])

  const hasResourceRole = useCallback((role: string, resource?: string): boolean => {
    if (DEV_BYPASS_KEYCLOAK) return true
    return keycloak.hasResourceRole(role, resource)
  }, [])

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    token: DEV_BYPASS_KEYCLOAK ? DEV_STATIC_TOKEN : keycloak.token,
    user,
    tokenParsed: DEV_BYPASS_KEYCLOAK ? bypassTokenParsed : keycloak.tokenParsed,
    error,
    logout,
    updateToken,
    hasRole,
    hasResourceRole,
  }

  // Handle loading and error states
  if (error) {
    return <>{errorComponent || <DefaultErrorScreen error={error} />}</>
  }

  if (isLoading) {
    return <>{loadingComponent}</>
  }

  if (!isAuthenticated) {
    return null // This shouldn't be reached with login-required
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Default Loading Screen
const DefaultLoadingScreen: FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Noto sans, sans-serif',
    }}
  >
    <div style={{ marginBottom: '20px' }}>
      <CircularProgress thickness={5} />
    </div>
    <h2>Authenticating...</h2>
    <p>Please wait while we verify your credentials</p>
  </div>
)

// Default Error Screen
const DefaultErrorScreen: FC<{ error?: Error }> = ({ error }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Noto sans, sans-serif',
    }}
  >
    <h2>Authentication Error</h2>
    <p>{error?.message || 'Failed to authenticate'}</p>
    <button
      onClick={() => window.location.reload()}
      style={{
        marginTop: '20px',
        padding: '10px 20px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
      }}
    >
      Retry
    </button>
  </div>
)
